import React from 'react';
import { CircleDot, Waves, Music, Sparkles, Heart, Eye } from 'lucide-react';
import SEO from '../components/SEO';
import './Beliefs.css';

const Beliefs = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Core Beliefs of Cosmergence Philosophy",
    "description": "Understand the six core beliefs of Cosmergence: continuity, conscious resonance, the eternal cycle, and what happens at death",
    "author": {
      "@type": "Organization",
      "name": "Cosmergence"
    }
  };

  const quickOverview = [
    {
      title: 'Continuity',
      description: 'Life and death are phases of one flow.'
    },
    {
      title: 'Conscious resonance',
      description: 'Consciousness is a harmonic pattern; echoes persist.'
    },
    {
      title: 'Ethic',
      description: 'Choose harmony over dissonance.'
    }
  ];

  const coreBeliefs = [
    {
      icon: <Sparkles size={36} />,
      title: 'Children of the cosmos',
      description: 'We are star‑forged matter, the universe aware of itself.'
    },
    {
      icon: <CircleDot size={36} />,
      title: 'Law of continuity',
      description: 'Energy/matter change form; nothing is lost.'
    },
    {
      icon: <Music size={36} />,
      title: 'Conscious resonance',
      description: 'Awareness as musical pattern; echoes can remain.'
    },
    {
      icon: <Waves size={36} />,
      title: 'Cycle',
      description: 'Coalescence → Expression → Dissolution → Reconstitution.'
    },
    {
      icon: <Heart size={36} />,
      title: 'Ethics as harmony',
      description: 'Balance, compassion, sustainability.'
    },
    {
      icon: <Eye size={36} />,
      title: 'Awe as practice',
      description: 'Science, art, and music as ways the cosmos recognizes itself.'
    }
  ];

  return (
    <div className="beliefs-page">
      <SEO
        title="Core Beliefs"
        description="What happens after death scientifically? Explore Cosmergence beliefs: law of continuity, conscious resonance, and the eternal cycle of energy through the cosmos. A science-based perspective on life and death."
        keywords="what happens after death scientifically, law of continuity, conscious resonance, philosophy of death, cosmic consciousness, energy transformation, secular beliefs about death"
        canonicalUrl="/beliefs"
        structuredData={structuredData}
      />
      
      {/* Hero Section */}
      <section className="beliefs-hero cosmic-bg">
        <div className="container">
          <h1 className="heading-1 text-center">Our Beliefs</h1>
          <p className="body-large text-center" style={{maxWidth: '700px', margin: '24px auto 0'}}>
            A philosophy rooted in science, wonder, and the eternal flow of energy through the cosmos.
          </p>
        </div>
      </section>

      {/* 30-Second Overview */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-2 text-center mb-8">30-Second Overview</h2>
          <div className="overview-grid">
            {quickOverview.map((item, index) => (
              <div key={index} className="overview-card">
                <h3 className="heading-3 text-gold">{item.title}</h3>
                <p className="body-regular">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Beliefs */}
      <section className="section-padding-small">
        <div className="container">
          <h2 className="heading-2 text-center mb-8">Core Beliefs</h2>
          <div className="grid-3">
            {coreBeliefs.map((belief, index) => (
              <div key={index} className="belief-card card hover-lift">
                <div className="belief-icon text-gold">
                  {belief.icon}
                </div>
                <h3 className="heading-3">{belief.title}</h3>
                <p className="body-regular">{belief.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cycle Diagram */}
      <section className="section-padding cosmic-bg">
        <div className="container">
          <h2 className="heading-2 text-center mb-8">The Eternal Cycle</h2>
          <div className="cycle-diagram">
            <div className="cycle-container">
              <svg viewBox="0 0 400 400" className="cycle-svg">
                <circle cx="200" cy="200" r="150" fill="none" stroke="var(--accent-gold)" strokeWidth="2" opacity="0.3" />
                
                <g className="cycle-node" transform="translate(200, 50)">
                  <circle cx="0" cy="0" r="40" fill="var(--bg-card)" stroke="var(--accent-gold)" strokeWidth="2" />
                  <text x="0" y="5" textAnchor="middle" fill="var(--text-primary)" fontSize="12" fontWeight="600">Coalescence</text>
                </g>
                
                <g className="cycle-node" transform="translate(330, 200)">
                  <circle cx="0" cy="0" r="40" fill="var(--bg-card)" stroke="var(--accent-gold)" strokeWidth="2" />
                  <text x="0" y="5" textAnchor="middle" fill="var(--text-primary)" fontSize="12" fontWeight="600">Expression</text>
                </g>
                
                <g className="cycle-node" transform="translate(200, 350)">
                  <circle cx="0" cy="0" r="40" fill="var(--bg-card)" stroke="var(--accent-gold)" strokeWidth="2" />
                  <text x="0" y="5" textAnchor="middle" fill="var(--text-primary)" fontSize="12" fontWeight="600">Dissolution</text>
                </g>
                
                <g className="cycle-node" transform="translate(70, 200)">
                  <circle cx="0" cy="0" r="40" fill="var(--bg-card)" stroke="var(--accent-gold)" strokeWidth="2" />
                  <text x="0" y="-5" textAnchor="middle" fill="var(--text-primary)" fontSize="11" fontWeight="600">Reconstitu-</text>
                  <text x="0" y="8" textAnchor="middle" fill="var(--text-primary)" fontSize="11" fontWeight="600">tion</text>
                </g>
                
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
                    <polygon points="0 0, 10 3, 0 6" fill="var(--accent-gold)" opacity="0.6" />
                  </marker>
                </defs>
                
                <path d="M 220 70 Q 280 120 310 180" fill="none" stroke="var(--accent-gold)" strokeWidth="2" opacity="0.6" markerEnd="url(#arrowhead)" />
                <path d="M 310 220 Q 280 280 220 330" fill="none" stroke="var(--accent-gold)" strokeWidth="2" opacity="0.6" markerEnd="url(#arrowhead)" />
                <path d="M 180 330 Q 120 280 90 220" fill="none" stroke="var(--accent-gold)" strokeWidth="2" opacity="0.6" markerEnd="url(#arrowhead)" />
                <path d="M 90 180 Q 120 120 180 70" fill="none" stroke="var(--accent-gold)" strokeWidth="2" opacity="0.6" markerEnd="url(#arrowhead)" />
              </svg>
            </div>
            <div className="cycle-description">
              <p className="body-large text-center" style={{maxWidth: '600px', margin: '32px auto 0'}}>
                The eternal cycle of existence: matter coalesces into form, expresses consciousness, dissolves back into the cosmic river, and reconstitutes into new patterns.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens at Death */}
      <section className="section-padding">
        <div className="container">
          <div className="death-section">
            <h2 className="heading-2 mb-4">What happens at death?</h2>
            <p className="body-large" style={{marginBottom: '24px'}}>
              Release your energy; flow back into the cosmic river. Atoms and elements rejoin the universe and may one day form new stars, worlds, and lives.
            </p>
            <p className="body-large">
              Personal ego ceases; resonant echoes remain within the whole.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Beliefs;